import React, {
  FunctionComponent,
  SyntheticEvent,
  useEffect,
  useState,
} from 'react';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { Container, Grid, Header } from 'semantic-ui-react';
import { useForm, FormProvider } from 'react-hook-form';
import { omit, isEmpty } from 'ramda';
import moment from 'moment';
import { format } from 'date-fns';
import classnames from 'classnames';
import withStyles from 'react-jss';

import FormSelectMCCList, {
  SelectionOption,
} from 'components/FormComponents/FormSelectMCCList'; // place this where specific widgets are located
import FormDateInput from 'components/FormComponents/FormDateInput';
import FormInput from 'components/FormComponents/FormInput';
import FormTextArea from 'components/FormComponents/FormTextArea';
import FormTimeInput from 'components/FormComponents/FormTimeInput';
import { GET_PROFILE } from 'components/UserMgmt/UserMgmt.queries';
import { UserProfileData } from 'components/UserMgmt/UserMgmt.types';
import { ClassProp } from 'helpers/types';
import DisplayTimeStamp from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/components/DisplayTimestamp';
import {
  MerchantCategoryCodes,
  handleError,
  handleSuccess,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/MerchantOffer.helpers';
import {
  buildTimeStamp,
  dateIsAfterCurrentDate,
  DATE_FORMAT,
  oneMinuteToMidnight,
  checkDates,
  getCurrentCSTDateWithTime,
  cstToUTC,
} from 'helpers/date';
import {
  OfferSummary,
  SetOfferPropertiesResponse,
  SetOfferPropertiesFormData,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/MerchantOffer.types';
import { GET_OFFER } from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/scenes/PulseOffer/PulseOffer.queries';

import { SET_OFFER_PROPERTIES } from './SetOfferProperties.mutations';
import styles from './SetOfferProperties.styles';

type Styles = ClassProp<ReturnType<typeof styles>>;

type Props = {
  classes: Styles;
  offer: OfferSummary;
  cancelUpdate: (updateOffer: boolean) => void;
};

const getSelectedMCCs = (MCCs: string) => {
  if (MCCs === '' || !MCCs) return [];
  const MCCsToArray = MCCs.split(',');
  const selectedMCCs: SelectionOption[] = [];
  MCCsToArray.map((code: string) => {
    MerchantCategoryCodes.options.filter((mcc: SelectionOption) => {
      if (mcc.value === code) {
        selectedMCCs.push(mcc);
      }
    });
  });

  return selectedMCCs;
};

const defaultSuccessMessage = [
  {
    displayMessage: 'Success!',
    message: 'Offer Properties Updated Successfully.',
  },
];

const SetOfferProperties: FunctionComponent<Props> = ({
  offer,
  cancelUpdate,
  classes,
}) => {
  const [userName, setUserName] = useState<string | null>(null);

  const { data, loading: profileLoading } = useQuery<UserProfileData>(
    GET_PROFILE,
  );

  useEffect(() => {
    if (profileLoading || data?.userProfile === undefined) return;
    const { userProfile } = data;
    const { givenName, familyName } = userProfile;
    setUserName(`${givenName} ${familyName}`);
  }, [data, profileLoading]);

  const {
    offerUuid,
    offerStartDate,
    offerEndDate,
    offerDescription,
    offerKeywords,
    paymentNetworkMCC,
  } = offer;

  const startDate = new Date(offerStartDate);
  const endDate = new Date(offerEndDate || '12/31/2035');
  const offerHasStarted = moment(startDate, 'MM/DD/YYYY HH:MM') < moment();

  const selectMCCs = getSelectedMCCs(paymentNetworkMCC);

  const methods = useForm<SetOfferPropertiesFormData>({
    mode: 'onBlur',
    defaultValues: {
      offerDescription,
      keywords: offerKeywords,
      startDate,
      startTime: format(startDate, 'HH:mm'),
      endDate,
      endTime: format(oneMinuteToMidnight(), 'HH:mm'),
    },
  });

  const {
    register,
    handleSubmit,
    getValues,
    formState,
    watch,
    trigger,
  } = methods;

  const endTime = watch('endTime');

  useEffect(() => {
    register(
      { name: 'startDate' },
      {
        validate: {
          isRequired: (value) => checkValue(value) || 'Start Date is required',
          oneOf: (value) =>
            checkDates(
              getCurrentCSTDateWithTime(value, getValues().startTime),
              getValues().endDate,
            ) || 'Start Date must come before End Date',
        },
      },
    );
    register(
      { name: 'endDate' },
      {
        validate: {
          isRequired: (value) => checkValue(value) || 'End Date is required',
          oneOf: (value) =>
            checkDates(
              getValues().startDate,
              getCurrentCSTDateWithTime(value, getValues().endTime),
            ) || 'End Date must come after Start Date',
          isAfter: (value) =>
            dateIsAfterCurrentDate(value) ||
            "End Date can't be earlier than today",
        },
      },
    );
    register(
      { name: 'startTime' },
      {
        validate: {
          isRequired: (value) => checkValue(value) || 'Start Time is required',
        },
      },
    );
    register(
      { name: 'endTime' },
      {
        validate: {
          isRequired: (value) => checkValue(value) || 'End Time is required',
        },
      },
    );
    register({ name: 'merchantCategoryCodes' });
    register({ name: 'merchantCategoryCodesToRemove' });
  }, [register]);

  useEffect(() => {
    trigger('endDate');
  }, [endTime]);

  const handleSetOfferPropertiesResponse = (
    response: SetOfferPropertiesResponse,
  ) => {
    handleSuccess(defaultSuccessMessage);
    cancelUpdate(false);
  };

  const [runMutation, { loading: isSubmitting }] = useMutation(
    SET_OFFER_PROPERTIES,
    {
      onCompleted: handleSetOfferPropertiesResponse,
      onError: handleError,
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GET_OFFER,
          variables: {
            params: {
              offerUuid,
              hydrateFinancialTerms: true,
            },
          },
        },
      ],
    },
  );

  const handleClick = (_: SyntheticEvent<HTMLElement, Event>) =>
    cancelUpdate(false);

  const onSubmit = ({
    offerUuid,
    startDate,
    startTime,
    endDate,
    endTime,
    offerDescription,
    keywords,
    merchantCategoryCodes = [],
    merchantCategoryCodesToRemove = [],
  }: SetOfferPropertiesFormData) => {
    const startTimeStamp = buildTimeStamp(
      format(startDate, DATE_FORMAT),
      startTime,
    );
    const endTimeStamp = buildTimeStamp(format(endDate, DATE_FORMAT), endTime);

    const properties = {
      offerUuid,
      keywords,
      offerDescription,
      merchantCategoryCodes,
      merchantCategoryCodesToRemove,
      startDate: cstToUTC(startTimeStamp),
      endDate: cstToUTC(endTimeStamp),
      updatedBy: userName,
    };
    let offerProperties;

    if (offerHasStarted) {
      offerProperties = omit(
        ['startDate', 'merchantCategoryCodes'],
        properties,
      );
    } else {
      offerProperties = { ...properties };
    }

    if (isEmpty(merchantCategoryCodesToRemove)) {
      offerProperties = omit(
        ['merchantCategoryCodesToRemove'],
        offerProperties,
      );
    }

    runMutation({
      variables: {
        params: { ...offerProperties },
      },
    });
  };

  const checkValue = (value: string) => {
    if (offerHasStarted) return true;

    if (!value) return false;

    return true;
  };

  return (
    <Container className={classes.container}>
      <Header as="h2">Update Offer</Header>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <input
            type="hidden"
            value={offerUuid}
            name="offerUuid"
            ref={register}
          />
          <Grid>
            {offerHasStarted ? (
              <Grid.Row>
                <Grid.Column width={8}>
                  <DisplayTimeStamp
                    label="Offer Start:"
                    date={offerStartDate}
                  />
                </Grid.Column>
              </Grid.Row>
            ) : (
              <Grid.Row>
                <Grid.Column width={4}>
                  <FormDateInput
                    name="startDate"
                    label="Start Date"
                    tabIndex={4}
                    placeholder={startDate}
                  />
                </Grid.Column>
                <Grid.Column width={4}>
                  <FormTimeInput
                    name="startTime"
                    label="Start Time (CST)"
                    tabIndex={5}
                    placeholder={startDate}
                  />
                </Grid.Column>
              </Grid.Row>
            )}
            <Grid.Row>
              <Grid.Column width={4}>
                <FormDateInput
                  name="endDate"
                  label="End Date"
                  tabIndex={6}
                  placeholder={endDate}
                />
              </Grid.Column>
              <Grid.Column width={4}>
                <FormTimeInput
                  name="endTime"
                  label="End Time (CST)"
                  tabIndex={7}
                  placeholder={endDate}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column>
                <FormTextArea
                  name="offerDescription"
                  type="text"
                  placeholder="Enter a brief description about the offer"
                  label="Offer Description"
                  tabIndex={2}
                  register={register()}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={10}>
                <FormInput
                  name="keywords"
                  type="text"
                  placeholder="Keywords used for search with in the app"
                  label="Offer Keywords"
                  tabIndex={11}
                  register={register}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={16}>
                <FormSelectMCCList
                  label="Merchant Category Codes"
                  name="merchantCategoryCodes"
                  dropDownOptions={MerchantCategoryCodes}
                  mccRemovalArrayName={'merchantCategoryCodesToRemove'}
                  defaultValues={selectMCCs}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row className={classes.formRow}>
              <button
                className={classnames(
                  classes.button,
                  classes.buttonSecondary,
                  classes.mediumBtn,
                )}
                onClick={handleClick}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !formState.isValid}
                className={classnames(classes.button, classes.mediumBtn)}
              >
                {isSubmitting ? '...Submitting' : 'Update Offer'}
              </button>
            </Grid.Row>
          </Grid>
        </form>
      </FormProvider>
    </Container>
  );
};

export default withStyles(styles)(SetOfferProperties);
